import { useEffect, type ReactNode } from 'react';
import { Breadcrumb, type Crumb } from './Breadcrumb.js';

export const PAGE_TITLE_ID = 'page-title';

export interface PageHeadProps {
  /** The one `<h1>` on the page. */
  title: string;
  /** One sentence explaining the page — never two (02-patterns §2.1). */
  subtitle?: ReactNode;
  /** Ancestry only; omit on a top-level page and the row is reserved empty. */
  crumbs?: readonly Crumb[];
  /** At most one primary button; the rest secondary or in an overflow. */
  actions?: ReactNode;
  /** Module section navigation, placed after the head and before the body (02-patterns §2.4). */
  strip?: ReactNode;
  /** Sets the document title; defaults to `${title} · Calis`. */
  documentTitle?: string;
  /** Rendered beside the title: a status pill, a source chip (06-modules §5.3 R1). */
  titleAside?: ReactNode;
  /** A quotable identifier line under the title, selectable (06-modules §5.3 R1). */
  identifier?: ReactNode;
  className?: string;
}

/**
 * The page head every page shares (02-patterns §2.1). The crumb row is always laid out — visibly
 * or as the same element made invisible — so the title lands at one height on every screen; the
 * head's gaps are fixed in components.css, not chosen per page.
 */
export function PageHead({ title, subtitle, crumbs, actions, strip, documentTitle, titleAside, identifier, className }: PageHeadProps) {
  useEffect(() => {
    document.title = documentTitle ?? `${title} · Calis`;
  }, [title, documentTitle]);
  const hasCrumbs = crumbs !== undefined && crumbs.length > 0;
  return (
    <header className={['page-head', strip ? 'page-head--with-strip' : '', className ?? ''].filter(Boolean).join(' ')}>
      <div className="page-head__main">
        <Breadcrumb items={crumbs ?? []} placeholder={!hasCrumbs} />
        <div className="page-head__title-row">
          <h1 id={PAGE_TITLE_ID} tabIndex={-1} className="page-head__title">{title}</h1>
          {titleAside ? <div className="page-head__aside">{titleAside}</div> : null}
        </div>
        {identifier ? <div className="page-head__identifier">{identifier}</div> : null}
        {subtitle ? <p className="page-head__subtitle">{subtitle}</p> : null}
        {actions ? <div className="page-head__actions">{actions}</div> : null}
      </div>
      {strip ? <div className="page-head__strip">{strip}</div> : null}
    </header>
  );
}

export type PageMeasure = 'reading' | 'read-wide' | 'wide';

export interface PageProps {
  children: ReactNode;
  /** Content measure (01-foundations §6.5). Dense class default is read-wide; tables may be wide. */
  measure?: PageMeasure;
  className?: string;
}

/** The page column: measure, gutters and the fixed head-to-body rhythm. */
export function Page({ children, measure = 'read-wide', className }: PageProps) {
  return <div className={['page', `page--${measure}`, className ?? ''].filter(Boolean).join(' ')}>{children}</div>;
}

/** A titled region within the page body: `<h2>` plus optional actions and description. */
export function Section({ title, description, actions, children, className, id }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; id?: string }) {
  return (
    <section className={['page-section', className].filter(Boolean).join(' ')} id={id} aria-labelledby={id ? `${id}-heading` : undefined}>
      <div className="page-section__head">
        <div className="page-section__titles">
          <h2 className="section-heading" id={id ? `${id}-heading` : undefined}>{title}</h2>
          {description ? <p className="page-section__description">{description}</p> : null}
        </div>
        {actions ? <div className="page-section__actions cluster">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}
